import { cutToast } from 'utility/toast';
import { useMutation } from '@apollo/client';
import { IconButton, Stack, TooltipHost } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/UrgencySelector/ConfirmDialog';
import { useCommonStyles } from 'common/styles';
import { SupplierUrgentUpdateInput } from 'common/types/globalTypes';
import React, { useState } from 'react';

import {
  SupplierUrgentUpdate,
  SupplierUrgentUpdateVariables,
} from './__generated__/SupplierUrgentUpdate';
import { useStyles } from './index.styles';
import { Supplier } from '../__generated__/Supplier';

import SUPPLIER_URGENCY_UPDATE from './SupplierUrgentUpdate.graphql';
interface UrgencyFolderProps {
  supplierDetailsData?: Supplier;
}

export const UrgencyFolder: React.FC<UrgencyFolderProps> = ({
  supplierDetailsData,
}) => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const [isVisible, setIsVisible] = useState(false);
  const isUrgent = !Boolean(supplierDetailsData?.supplier?._urgencyLevel);
  const urgencyLevelToolTip = isUrgent ? 'Remove Urgency' : 'Elevate to urgent';
  const iconName = isUrgent ? 'FabricFolderFill' : 'FabricFolder';
  const iconStyle = isUrgent ? commonStyles.urgentBox : styles.standardBox;
  const isChecked = !isUrgent;
  const [supplierUrgencyUpdate] = useMutation<
    SupplierUrgentUpdate,
    SupplierUrgentUpdateVariables
  >(SUPPLIER_URGENCY_UPDATE, { errorPolicy: 'all' });

  const onSubmit = async (requestComment: string | undefined | null) => {
    const updatedUrgencyLevel =
      supplierDetailsData?.supplier?._urgencyLevel === 0 ? 1 : 0;

    const { errors } = await supplierUrgencyUpdate({
      variables: {
        input: {
          entityId: supplierDetailsData?.supplier?.id!,
          rowTimestamp: supplierDetailsData?.supplier?._rowTimestamp,
          urgencyLevel: updatedUrgencyLevel,
          explanationComment: requestComment,
        } as SupplierUrgentUpdateInput,
      },
      update(cache, { data }) {
        if (!!supplierDetailsData?.supplier?.id) {
          cache.modify({
            id: cache.identify({ ...supplierDetailsData.supplier }),
            fields: {
              _urgencyLevel: () => {
                return data?.supplierUrgentUpdate?._newUrgentLevel;
              },
            },
          });
        }
        setIsVisible(false);
      },
    });
    if (errors?.length) cutToast.error(errors[0].message);
    else {
      cutToast.success('Urgency status updated successfully.');
    }
  };

  return (
    <Stack>
      <TooltipHost content={urgencyLevelToolTip}>
        <IconButton
          iconProps={{
            iconName: iconName,
          }}
          className={iconStyle}
          onClick={() => setIsVisible(true)}
          styles={{
            rootHovered: { color: 'redDark' },
            rootPressed: { color: 'redDark' },
          }}
        />
      </TooltipHost>
      <ConfirmDialog
        hidden={!isVisible}
        minWidth={500}
        checked={isChecked}
        onConfirm={(requestComment: string | null | undefined) =>
          onSubmit(requestComment)
        }
        onDismiss={() => setIsVisible(false)}
        onCancel={() => setIsVisible(false)}
      />
    </Stack>
  );
};
